import React, { useEffect } from "react";
import { IAddShippingAddressDrawerProps } from "./interface";
import { useSelector } from "react-redux";
import Form from "antd/lib/form";
import ZDrawer from "@molecules/ZDrawer";
import ZForm from "@organisms/ZForm";
import ZAlert from "@atoms/ZAlert";
import { Address, ShippingAddressFormItems } from "@/Contracts";
import { CustomField } from "@/Contracts/Settings";
import {
	EWidgets,
	IFormFieldProps,
	IFormProps,
	IFormSectionProps
} from "@organisms/ZForm/interface";
import { capitalizeFirstLetter } from "@/Contracts/Utils";
import { isValidAddress, normalizeZipcode } from "@/V2Utils";
import { Divider } from "antd";
import ZMessage from "@molecules/ZMessage";
import { GlobalState } from "@/Reducers/interface";
import "./index.less";
import { useFeatureFlag } from "@/Hooks/useFeature";
import {
	generateAddressFieldItems,
	extractAddressCustomFields
} from "@templates/ZDrawers/ZClients/ZClient/addressFieldUtils";
import { processCustomFieldFormValues } from "@/Utils";
import moment from "moment";

export const AddShippingAddressDrawer: React.FC<
	IAddShippingAddressDrawerProps
> = ({
	visible,
	onClose,
	shippingAddresses,
	currentActiveShippingAddress,
	onSubmit,
	isEditMode,
	showDocumentShiptoAddressNote = true,
	orderedAddressFields = []
}) => {
	const { isLoading } = useSelector((state: GlobalState) => ({
		isLoading: state.clients.isLoading
	}));

	const [form] = Form.useForm();

	const shouldSendIdentifier = useFeatureFlag("send_id_to_address");

	const handleClose = () => {
		form.resetFields();
		onClose && onClose();
	};

	useEffect(() => {
		if (visible) {
			form.resetFields();
			// Populate form with current address data if editing
			if (isEditMode && currentActiveShippingAddress) {
				const formValues: Record<string, any> = {};

				// Set system field values
				if (currentActiveShippingAddress.address_line_1)
					formValues.address_line_1 =
						currentActiveShippingAddress.address_line_1;
				if (currentActiveShippingAddress.address_line_2)
					formValues.address_line_2 =
						currentActiveShippingAddress.address_line_2;
				if (currentActiveShippingAddress.city)
					formValues.city = currentActiveShippingAddress.city;
				if (currentActiveShippingAddress.state)
					formValues.state = currentActiveShippingAddress.state;
				if (currentActiveShippingAddress.country)
					formValues.country = currentActiveShippingAddress.country;
				if (currentActiveShippingAddress.pincode)
					formValues.pincode = currentActiveShippingAddress.pincode;
				if (currentActiveShippingAddress.phone)
					formValues.phone = currentActiveShippingAddress.phone;
				if (currentActiveShippingAddress.is_default !== undefined)
					formValues.is_default =
						currentActiveShippingAddress.is_default;

				// Set custom field values
				if (currentActiveShippingAddress.custom_fields?.length) {
					currentActiveShippingAddress.custom_fields.forEach(
						customField => {
							if (customField.values?.length) {
								// For multiselect fields, use the entire values array
								if (
									customField.field_type ===
									"multi_select_field"
								) {
									formValues[`custom_${customField.id}`] =
										customField.values;
								} else {
									const value = customField.values[0];
									// Convert date fields to moment objects
									if (
										customField.field_type ===
											"date_field" &&
										value
									) {
										const momentValue = moment(value);
										formValues[`custom_${customField.id}`] =
											momentValue.isValid()
												? momentValue
												: undefined;
									} else {
										formValues[`custom_${customField.id}`] =
											value;
									}
								}
							}
						}
					);
				}

				// Set form values after a brief delay to ensure form is ready
				setTimeout(() => {
					form.setFieldsValue(formValues);
				}, 100);
			}
		}
	}, [visible, isEditMode, currentActiveShippingAddress, form]);

	const initialValues = (field: CustomField) => {
		if (!currentActiveShippingAddress) {
			// For multiselect fields, return empty array
			if (field.field_type === "multi_select_field") {
				return [];
			}
			// For dropdown and currency fields, return undefined to show placeholder
			if (
				field.field_type === "dropdown_field" ||
				field.field_type === "currency_field"
			) {
				return undefined;
			}
			return "";
		}

		// Handle system fields
		if (field.is_system_field && field.system_field_id) {
			// Backend now uses unprefixed IDs: "city", "state", "country", "pincode", "phone"
			// These match Address object properties directly
			const systemFieldId = field.system_field_id;

			// Direct property access using system_field_id
			if (systemFieldId in currentActiveShippingAddress) {
				return (
					currentActiveShippingAddress[
						systemFieldId as keyof Address
					] || ""
				);
			}

			return "";
		}

		// Handle custom fields - now stored as CustomField array
		const customField = currentActiveShippingAddress.custom_fields?.find(
			cf => cf.id === field.id
		);

		// For multiselect fields, return the entire values array
		if (field.field_type === "multi_select_field") {
			return customField?.values || [];
		}

		const value = customField?.values?.[0];

		// Convert date fields to moment objects
		if (value && field.field_type === "date_field") {
			const momentValue = moment(value);
			return momentValue.isValid() ? momentValue : "";
		}

		// For dropdown and currency fields, return undefined if no value to show placeholder
		if (
			field.field_type === "dropdown_field" ||
			field.field_type === "currency_field"
		) {
			return value || undefined;
		}

		return value || "";
	};

	const handleAddSubmit = (formValues: any) => {
		if (isValidAddress(formValues)) {
			form.resetFields();
			ZMessage.error("Please enter at least one field");
			return;
		}

		// Extract custom fields from form values
		const customFieldsFormValues = extractAddressCustomFields(
			formValues,
			orderedAddressFields
		);

		// Create address object
		const addShippingAddress: Address = {
			...new Address(),
			address_line_1: formValues.address_line_1 || undefined,
			address_line_2: formValues.address_line_2 || undefined,
			city: formValues.city || undefined,
			state: formValues.state || undefined,
			pincode: formValues.pincode || undefined,
			country: formValues.country || undefined,
			phone: formValues.phone || undefined,
			is_default: formValues.is_default || false
		};

		// Add custom fields in the correct format
		if (
			Object.keys(customFieldsFormValues).length > 0 &&
			orderedAddressFields?.length > 0
		) {
			addShippingAddress.custom_fields = processCustomFieldFormValues(
				customFieldsFormValues,
				orderedAddressFields.filter(field => !field.is_system_field)
			);
		}

		onSubmit(addShippingAddress);
	};

	const handleEditSubmit = (formValues: any) => {
		if (isValidAddress(formValues)) {
			form.resetFields();
			ZMessage.error("Please enter at least one field");
			return;
		}

		// Extract custom fields from form values
		const customFieldsFormValues = extractAddressCustomFields(
			formValues,
			orderedAddressFields
		);

		// Create updated address object
		let updatedShippingAddress: Address = {
			...new Address(),
			address_line_1: formValues.address_line_1 || undefined,
			address_line_2: formValues.address_line_2 || undefined,
			city: formValues.city || undefined,
			state: formValues.state || undefined,
			pincode: formValues.pincode || undefined,
			country: formValues.country || undefined,
			phone: formValues.phone || undefined,
			is_default: formValues.is_default || false
		};

		// Add custom fields in the correct format
		if (
			Object.keys(customFieldsFormValues).length > 0 &&
			orderedAddressFields?.length > 0
		) {
			updatedShippingAddress.custom_fields = processCustomFieldFormValues(
				customFieldsFormValues,
				orderedAddressFields.filter(field => !field.is_system_field)
			);
		}

		if (!!currentActiveShippingAddress?.sync_data?.length) {
			updatedShippingAddress = {
				...updatedShippingAddress,
				sync_data: [...currentActiveShippingAddress?.sync_data]
			};
		}
		if (shouldSendIdentifier) {
			updatedShippingAddress.id = shouldSendIdentifier
				? currentActiveShippingAddress?.id ?? null
				: null;
		}
		onSubmit(updatedShippingAddress);
	};

	const getShippingAddressFormItems = () => {
		// Use API-based ordering if available, otherwise fallback to hard-coded order
		if (orderedAddressFields?.length > 0) {
			// Check if API response contains system fields
			const hasSystemFields = orderedAddressFields.some(
				field => field.is_system_field
			);

			if (hasSystemFields) {
				// Use API-provided field order (includes system fields)
				const items = generateAddressFieldItems(
					orderedAddressFields,
					"shipping",
					initialValues,
					true // isShippingAddress = true for different placeholders
				);
				return items.filter(Boolean); // Filter out any null values
			} else {
				// API has custom fields but no system fields - combine fallback system fields + API custom fields
				const systemFields = Object.values(
					ShippingAddressFormItems
				).map(entity => ({
					key: entity,
					label:
						entity === ShippingAddressFormItems.Pincode
							? "Zip Code"
							: entity === ShippingAddressFormItems.AddressLine1
							? "Street"
							: entity === ShippingAddressFormItems.AddressLine2
							? "Street 2"
							: capitalizeFirstLetter(entity),
					hasFeedback: true,
					normalize:
						entity === ShippingAddressFormItems.Pincode
							? normalizeZipcode
							: undefined,
					widget: EWidgets.INPUT,
					widgetProps: {
						width: "full",
						placeholder: `Please enter shipping ${
							entity === ShippingAddressFormItems.Pincode
								? "zip code"
								: entity ===
								  ShippingAddressFormItems.AddressLine1
								? "street"
								: entity ===
								  ShippingAddressFormItems.AddressLine2
								? "street 2"
								: `${entity} name`
						}`
					}
				}));

				const customFieldItems = generateAddressFieldItems(
					orderedAddressFields.filter(
						field => !field.is_system_field
					),
					"shipping",
					initialValues,
					true // isShippingAddress = true for different placeholders
				).filter(Boolean);

				// Combine: system fields first, then custom fields
				return [...systemFields, ...customFieldItems];
			}
		}

		// Fallback to original hard-coded implementation
		return Object.values(ShippingAddressFormItems).map(entity => ({
			key: entity,
			label:
				entity === ShippingAddressFormItems.Pincode
					? "Zip Code"
					: entity === ShippingAddressFormItems.AddressLine1
					? "Street"
					: entity === ShippingAddressFormItems.AddressLine2
					? "Street 2"
					: capitalizeFirstLetter(entity),
			hasFeedback: true,
			normalize:
				entity === ShippingAddressFormItems.Pincode
					? normalizeZipcode
					: undefined,
			initialValue: "",
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: `Please enter shipping ${
					entity === ShippingAddressFormItems.Pincode
						? "zip code"
						: entity === ShippingAddressFormItems.AddressLine1
						? "street"
						: entity === ShippingAddressFormItems.AddressLine2
						? "street 2"
						: `${entity} name`
				}`
			}
		}));
	};

	const checkBoxBoolean = !!currentActiveShippingAddress?.is_default
		? true
		: !shippingAddresses.length;

	const isDocumentShipAddressEnabled = useFeatureFlag(
		"document_ship_to_address"
	);

	const getShippingAddressFormItemsWithCheckBox = () => {
		return [
			...(isEditMode &&
			isDocumentShipAddressEnabled &&
			showDocumentShiptoAddressNote
				? [
						{
							key: "document-level-address-info",
							render: () => (
								<ZAlert
									message="The changes will be made to the client's shipping address only. All the documents and sales automations using this address as ship to address will not be affected until you change it in document details."
									showIcon={true}
									type="info"
									className="document-level-address-info"
								/>
							)
						}
				  ]
				: []),
			...getShippingAddressFormItems(),
			{
				key: "is_default",
				widget: EWidgets.CHECKBOX,
				children: "Set as Default Address",
				initialValue: checkBoxBoolean,
				disabled: checkBoxBoolean
			},
			{
				key: "divider",
				render: () => <Divider />
			}
		].filter(Boolean) as IFormFieldProps[];
	};

	const sections: IFormSectionProps[] = [
		{
			meta: {
				formItemLayout: [5, 19],
				fields: getShippingAddressFormItemsWithCheckBox()
			}
		}
	];

	const formProps: IFormProps = {
		onFinish: isEditMode ? handleEditSubmit : handleAddSubmit,
		key: "ShippingAddressDrawerForm",
		sections,
		loading: isLoading,
		submitButtonText: "Save",
		isStickFooter: true,
		form,
		isCancelAllowed: true,
		onCancel: handleClose,
		isRightFooter: true,
		className: "shipping-address-edit-drawer"
	};

	return (
		<ZDrawer
			visible={visible}
			closable={true}
			destroyOnClose={true}
			onClose={handleClose}
			title={`${isEditMode ? "Edit" : "Add"} Shipping Address`}
			removeDefaultPadding={true}
		>
			<ZForm {...formProps} />
		</ZDrawer>
	);
};

export default AddShippingAddressDrawer;
